<template>
  <div
    class="lm-win"
    :class="{'lm-win-max': win.maximized}"
    :style="winStyle"
    @mousedown="onFocus">
    <div
      class="lm-title"
      @mouseenter="showTitleTip"
      @mouseleave="hideTitleTip"
      @click="showTitleTip">
      <div class="lm-title-text">{{ titleText }}</div>
      <div class="lm-title-actions">
        <Button v-if="win.lesson.liveUrl" size="small" type="text" @click.stop="onOpenExternal">新窗口打开</Button>
      </div>
    </div>
    <div
      v-show="tipVisible"
      class="lm-title-tip"
      :style="tipStyle"
      @mouseenter="showTitleTip"
      @mouseleave="hideTitleTip">{{ titleText }}</div>
    <div class="lm-body" @click="onBodyClick">
      <video
        v-if="win.playing"
        ref="player"
        class="lm-video"
        autoplay
        muted
        playsinline
        controls></video>
      <div v-else class="lm-placeholder">
        {{ placeholderText }}
      </div>
    </div>
  </div>
</template>
<script>
const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.17/dist/hls.min.js'

export default {
  name: 'LiveMonitorWindow',
  props: {
    win: {
      type: Object,
      required: true
    }
  },
  computed: {
    winStyle: function () {
      return {
        left: this.win.left + 'px',
        top: this.win.top + 'px',
        width: this.win.width + 'px',
        height: this.win.height + 'px',
        zIndex: this.win.zIndex
      }
    },
    titleText: function () {
      let lesson = this.win.lesson || {}
      let name = lesson.userNickname || '学生'
      let uid = lesson.uid != null ? lesson.uid : ''
      let time = this.formatTimeRange(lesson.lsnBeginTime, lesson.lsnEndTime)
      let tch = this.formatTchName(lesson)
      let title = lesson.lessonTitle || ''
      let adviser = lesson.studyAdviserNickname || '未分配'
      let sale = lesson.adviserNickname || '未分配'
      return name + '(' + uid + ')  ' + time + '  ' + tch + '  ' + title +
        '  班主任' + adviser + '  销售' + sale
    },
    placeholderText: function () {
      if (this.win.lesson && this.win.lesson.liveHls) {
        return '点击播放'
      }
      if (this.win.lesson && this.win.lesson.liveUrl) {
        return '无拉流'
      }
      return '未同步 / 未开播'
    },
    playerUrl: function () {
      return this.win.lesson && this.win.lesson.liveHls ? this.win.lesson.liveHls : ''
    }
  },
  data: function () {
    return {
      hls: null,
      tipVisible: false,
      tipStyle: {},
      tipTimer: null
    }
  },
  watch: {
    'win.playing': function (val) {
      let self = this
      this.$nextTick(function () {
        if (val) {
          self.attachPlayer()
        } else {
          self.detachPlayer()
        }
      })
    }
  },
  mounted: function () {
    if (this.win.playing) {
      this.$nextTick(this.attachPlayer)
    }
  },
  beforeDestroy: function () {
    this.detachPlayer()
    this.clearTipTimer()
  },
  methods: {
    formatTimeRange: function (begin, end) {
      let a = begin ? this.$moment.unix(begin).format('HH:mm') : '--:--'
      let b = end ? this.$moment.unix(end).format('HH:mm') : '--:--'
      return a + '-' + b
    },
    formatTchName: function (lesson) {
      if (!lesson) {
        return ''
      }
      if (lesson.tchName) {
        return lesson.tchName
      }
      let first = lesson.tchFirstName || ''
      let last = lesson.tchLastName || ''
      return (first + ' ' + last).trim()
    },
    onFocus: function () {
      this.$emit('focus', this.win)
    },
    clearTipTimer: function () {
      if (this.tipTimer) {
        clearTimeout(this.tipTimer)
        this.tipTimer = null
      }
    },
    showTitleTip: function () {
      this.clearTipTimer()
      this.$emit('focus', this.win)
      let el = this.$el
      if (!el) {
        return
      }
      let title = el.querySelector('.lm-title')
      let rect = title ? title.getBoundingClientRect() : el.getBoundingClientRect()
      this.tipStyle = {
        left: rect.left + 'px',
        top: (rect.bottom + 4) + 'px',
        maxWidth: Math.max(rect.width, 360) + 'px'
      }
      this.tipVisible = true
    },
    hideTitleTip: function () {
      let self = this
      this.clearTipTimer()
      this.tipTimer = setTimeout(function () {
        self.tipVisible = false
        self.tipTimer = null
      }, 200)
    },
    onOpenExternal: function () {
      if (this.win.lesson && this.win.lesson.liveUrl) {
        window.open(this.win.lesson.liveUrl)
        return
      }
      this.$emit('open-external', this.win)
    },
    loadHlsScript: function (cb) {
      if (window.Hls) {
        cb()
        return
      }
      if (window.__lmHlsLoading) {
        window.__lmHlsLoading.push(cb)
        return
      }
      window.__lmHlsLoading = [cb]
      let script = document.createElement('script')
      script.src = HLS_CDN
      script.onload = function () {
        let list = window.__lmHlsLoading || []
        window.__lmHlsLoading = null
        for (let i = 0; i < list.length; i++) {
          list[i]()
        }
      }
      script.onerror = function () {
        window.__lmHlsLoading = null
      }
      document.head.appendChild(script)
    },
    attachPlayer: function () {
      let video = this.$refs.player
      if (!video) {
        return
      }
      this.detachPlayer()
      if (!this.playerUrl) {
        return
      }
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = this.playerUrl
        video.play()
        return
      }
      let self = this
      this.loadHlsScript(function () {
        if (!window.Hls || !window.Hls.isSupported()) {
          return
        }
        if (!self.win.playing || !self.$refs.player || !self.playerUrl) {
          return
        }
        self.hls = new window.Hls()
        self.hls.loadSource(self.playerUrl)
        self.hls.attachMedia(self.$refs.player)
        self.hls.on(window.Hls.Events.MANIFEST_PARSED, function () {
          if (self.$refs.player) {
            self.$refs.player.play()
          }
        })
      })
    },
    detachPlayer: function () {
      if (this.hls) {
        this.hls.destroy()
        this.hls = null
      }
      let video = this.$refs.player
      if (video) {
        video.pause()
        video.removeAttribute('src')
        video.load()
      }
    },
    onBodyClick: function () {
      if (!this.win.playing && this.win.lesson && this.win.lesson.liveHls) {
        this.$emit('play', this.win)
      }
    }
  }
}
</script>
<style scoped>
.lm-win {
  position: absolute;
  background: #fff;
  border: 1px solid #dddee1;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
.lm-title {
  height: 36px;
  line-height: 36px;
  padding: 0 6px 0 10px;
  background: #2d8cf0;
  color: #fff;
  cursor: default;
  display: flex;
  align-items: center;
  user-select: none;
  flex-shrink: 0;
}
.lm-title-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}
.lm-title-actions {
  flex-shrink: 0;
  margin-left: 8px;
  cursor: default;
}
.lm-title-actions >>> .ivu-btn {
  color: #fff;
  height: 24px;
  padding: 0 6px;
}
.lm-title-actions >>> .ivu-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
}
.lm-body {
  position: relative;
  flex: 1;
  min-height: 0;
  background: #1c2438;
}
.lm-video {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}
.lm-placeholder {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbbec4;
  font-size: 14px;
  cursor: pointer;
}
.lm-title-tip {
  position: fixed;
  z-index: 4000;
  padding: 8px 10px;
  background: #fff;
  color: #17233d;
  border: 1px solid #dddee1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  font-size: 12px;
  line-height: 18px;
  white-space: normal;
  word-break: break-all;
}
</style>



// WEBPACK FOOTER //
// LiveMonitorWindow.vue?00536887